import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Inline "you were signed out" notice for the login page.
 *
 * When a session ends anywhere in the app, `authInterceptor` redirects to
 * `/onecare/login?redirect=...&error=<message>`. Previously nothing on the login
 * page read that `error` param, so the only feedback the user got was the
 * floating <AuthStatusIndicator> toast — a light-on-dark alert box that covered
 * the OneCare logo and offered a redundant "Log in" button while the user was
 * already looking at the login form.
 *
 * This component surfaces the same information *inside* the login card, in the
 * page's own glass/dark visual language (same shape as PreLoginDisclaimerBanner),
 * and strips `error` from the URL so a refresh doesn't resurrect it.
 */

type Tone = 'neutral' | 'denied';

interface NoticeCopy {
  tone: Tone;
  title: string;
  body: string;
}

/** Turn the raw interceptor message into a calmer, human title + body. */
function classify(rawMessage: string, willReturn: boolean): NoticeCopy {
  const msg = rawMessage.toLowerCase();

  const backTo = willReturn
    ? ' Sign in again and we will take you back to where you left off.'
    : ' Sign in again to continue.';

  // Timed-out sessions come from useSessionTimeout as stable tokens rather than
  // prose, and are checked first so the fuzzy matching below cannot claim them.
  //
  // Which timer fired is worth telling apart. "You were inactive" is something
  // the user could have prevented; a fixed maximum length is not, and being
  // told the wrong one is worse than being told nothing — it reads as an
  // accusation of having wandered off when they were working the whole time.
  if (msg.includes('session_ended_max_duration')) {
    return {
      tone: 'neutral',
      title: 'Your session reached its time limit',
      body:
        'Sessions end after a set length of time, however active you have been. ' +
        'This is a security setting for the hospital, not a problem with your account.' +
        backTo,
    };
  }

  if (msg.includes('session_ended_idle')) {
    return {
      tone: 'neutral',
      title: 'You were signed out for inactivity',
      body:
        'Your session ended because there was no activity for a while, so nobody ' +
        'else could use this screen.' + backTo,
    };
  }

  if (msg.includes('permission')) {
    return {
      tone: 'denied',
      title: 'Access restricted',
      body: 'You do not have permission to open that page. Sign in with an account that does.',
    };
  }

  if (msg.includes('refresh')) {
    return {
      tone: 'neutral',
      title: 'Your session could not be renewed',
      body: 'We were unable to keep your session alive in the background.' + backTo,
    };
  }

  if (msg.includes('confirm') || msg.includes('verification') || msg.includes('failed')) {
    return {
      tone: 'neutral',
      title: 'We could not confirm your session',
      body: 'For your security you were signed out.' + backTo,
    };
  }

  return {
    tone: 'neutral',
    title: 'Your session ended',
    body: 'You were signed out to keep your account secure.' + backTo,
  };
}

const LockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

export const SessionEndedNotice: React.FC<{ className?: string }> = ({ className }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawError = searchParams.get('error');
  const hasReturnPath = !!(searchParams.get('redirect') || searchParams.get('next'));

  // Capture the message once, then clean the URL so refreshing the login page
  // (or bookmarking it) does not replay a stale notice.
  const [copy, setCopy] = useState<NoticeCopy | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!rawError) return;

    setCopy(classify(rawError, hasReturnPath));
    setVisible(true);

    const next = new URLSearchParams(searchParams);
    next.delete('error');
    setSearchParams(next, { replace: true });
    // Only react to the arrival of an `error` param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawError]);

  if (!copy || !visible) return null;

  const isDenied = copy.tone === 'denied';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`auth-notice-in flex items-start gap-2.5 rounded-xl border p-3 backdrop-blur-sm ${
        isDenied
          ? 'border-rose-400/25 bg-rose-500/10'
          : 'border-white/15 bg-white/[0.06]'
      } ${className ?? ''}`}
    >
      <span
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ring-1 ${
          isDenied
            ? 'bg-rose-400/15 text-rose-200 ring-rose-300/30'
            : 'bg-cyan-400/15 text-cyan-200 ring-cyan-300/30'
        }`}
      >
        <LockIcon className="h-3 w-3" />
      </span>

      <div className="min-w-0 flex-1">
        <p className={`text-xs font-semibold ${isDenied ? 'text-rose-100' : 'text-white'}`}>
          {copy.title}
        </p>
        <p className={`mt-0.5 text-xs leading-snug ${isDenied ? 'text-rose-100/80' : 'text-slate-300'}`}>
          {copy.body}
        </p>
      </div>

      <button
        type="button"
        onClick={() => setVisible(false)}
        aria-label="Dismiss notice"
        className="-m-1 flex-shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/50"
      >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default SessionEndedNotice;
